import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import BadgeManagementModal from './BadgeManagementModal';
import { supabase } from '../../../lib/supabase';

const UserManagementTable = ({ users, onUserAction }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedUser, setSelectedUser] = useState(null);
  const [showBadgeModal, setShowBadgeModal] = useState(false);
  const [showRoleModal, setShowRoleModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showProfileModal, setShowProfileModal] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [enrichedData, setEnrichedData] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState({ tc: '', phone: '', city: '', district: '', address: '' });

  // Helper: determine if a user is banned from either isBanned boolean or status string
  const isUserBanned = (user) => !!(user?.isBanned || user?.status === 'banned');

  const roleOptions = [
    { value: 'all', label: 'Tüm Roller' },
    { value: 'user', label: 'Kullanıcı' },
    { value: 'organizer', label: 'Organizatör' },
    { value: 'admin', label: 'Admin' }
  ];

  const statusOptions = [
    { value: 'all', label: 'Tüm Durumlar' },
    { value: 'active', label: 'Aktif' },
    { value: 'banned', label: 'Yasaklı' },
    { value: 'pro', label: 'PRO Üye' }
  ];

  const filteredUsers = users?.filter(user => {
    const matchesSearch = user?.name?.toLowerCase()?.includes(searchTerm?.toLowerCase()) ||
                         user?.email?.toLowerCase()?.includes(searchTerm?.toLowerCase());
    const matchesRole = roleFilter === 'all' || user?.role === roleFilter;
    const matchesStatus = statusFilter === 'all' || 
      (statusFilter === 'banned' ? isUserBanned(user) : user?.status === statusFilter);
    return matchesSearch && matchesRole && matchesStatus;
  });

  const handleManageBadges = (user) => {
    setSelectedUser(user);
    setShowBadgeModal(true);
  };

  const handleBadgeModalClose = () => {
    setShowBadgeModal(false);
    setSelectedUser(null);
  };

  const handleBadgeUpdate = () => {
    if (onUserAction) {
      onUserAction('refresh');
    }
  };

  const handleRoleChange = async (user, newRole) => {
    setIsProcessing(true);
    try {
      await onUserAction('role', user?.id, { role: newRole });
      setShowRoleModal(false);
      setSelectedUser(null);
    } catch (error) {
      console.error('Role change error:', error);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleBanToggle = async (user) => {
    setIsProcessing(true);
    try {
      const action = isUserBanned(user) ? 'unban' : 'ban';
      await onUserAction(action, user?.id);
    } catch (error) {
      console.error('Ban toggle error:', error);
    } finally {
      setIsProcessing(false);
    }
  };

  const handlePromoteUser = async (user) => {
    setIsProcessing(true);
    try {
      await onUserAction('promote', user?.id);
    } catch (error) {
      console.error('Promote error:', error);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleDeleteUser = async () => {
    setIsProcessing(true);
    try {
      await onUserAction('delete', selectedUser?.id);
      setShowDeleteModal(false);
      setSelectedUser(null);
    } catch (error) {
      console.error('Delete error:', error);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleViewProfile = (user) => {
    setSelectedUser(user);
    setShowProfileModal(true);
  };

  useEffect(() => {
    if (!showProfileModal || !selectedUser) {
      setEnrichedData(null);
      setIsEditing(false);
      return;
    }

    let isMounted = true;

    const loadUserData = async () => {
      if (!selectedUser) return;

      // Fetch the latest order to get the rich JSON data
      const { data: latestOrder, error } = await supabase?.from('orders')?.select('shipping_address, tc_kimlik, phone')?.or(`user_id.eq.${selectedUser?.id},email.eq.${selectedUser?.email}`)?.order('created_at', { ascending: false })?.limit(1)?.single();

      if (latestOrder && isMounted) {
        let addr = {};
        try {
          addr = typeof latestOrder?.shipping_address === 'string'
            ? JSON.parse(latestOrder?.shipping_address)
            : (latestOrder?.shipping_address || {});
        } catch (e) {}

        // Extract values using all possible fallback keys
        const foundTc = latestOrder?.tc_kimlik || addr?.tc || addr?.tc_kimlik || addr?.identityNumber || addr?.vkn || '';
        const foundCity = addr?.city || addr?.il || '';
        const foundDistrict = addr?.district || addr?.ilce || '';
        const foundAddress = addr?.fullAddress || addr?.address || addr?.acikAdres || addr?.full_address || '';
        const foundPhone = latestOrder?.phone || addr?.phone || addr?.telefon || selectedUser?.phone || '';

        // Update the state that drives the UI (both display and edit form)
        setEnrichedData({
          tc: foundTc,
          city: foundCity,
          district: foundDistrict,
          address: foundAddress,
          phone: foundPhone
        });

        // Pre-fill the edit form with this data too
        setFormData({
          tc: foundTc || selectedUser?.tc_kimlik || selectedUser?.checkout_tc || '',
          phone: foundPhone || selectedUser?.checkout_phone || '',
          city: foundCity || selectedUser?.city || selectedUser?.checkout_city || '',
          district: foundDistrict || selectedUser?.district || selectedUser?.checkout_district || '',
          address: foundAddress || selectedUser?.full_address || selectedUser?.checkout_address || '',
        });
      } else if (isMounted) {
        // No order found — fall back to profile/checkout data
        setEnrichedData({
          tc: selectedUser?.tc_kimlik || selectedUser?.checkout_tc || null,
          city: selectedUser?.city || selectedUser?.checkout_city || null,
          district: selectedUser?.district || selectedUser?.checkout_district || null,
          address: selectedUser?.full_address || selectedUser?.checkout_address || null,
          phone: selectedUser?.phone || selectedUser?.checkout_phone || null,
        });
        setFormData({
          tc: selectedUser?.tc_kimlik || selectedUser?.checkout_tc || '',
          phone: selectedUser?.phone || selectedUser?.checkout_phone || '',
          city: selectedUser?.city || selectedUser?.checkout_city || '',
          district: selectedUser?.district || selectedUser?.checkout_district || '',
          address: selectedUser?.full_address || selectedUser?.checkout_address || '',
        });
      }
    };

    loadUserData();

    return () => { isMounted = false; };
  }, [selectedUser]);

  const handleSaveProfile = async () => {
    setIsProcessing(true);
    try {
      // 1. Get the user's most recent order
      const { data: lastOrder, error: fetchError } = await supabase?.from('orders')?.select('*')?.or(`user_id.eq.${selectedUser?.id},email.eq.${selectedUser?.email}`)?.order('created_at', { ascending: false })?.limit(1)?.single();

      if (!lastOrder) {
        alert("Bu e-posta adresine ait bir sipariş bulunamadı. Adres verisi sadece siparişler üzerine kaydedilebilir.");
        setIsProcessing(false);
        return;
      }

      // 2. Parse existing shipping_address JSON
      let currentShipping = {};
      try {
        currentShipping = typeof lastOrder?.shipping_address === 'string'
          ? JSON.parse(lastOrder?.shipping_address)
          : (lastOrder?.shipping_address || {});
      } catch (e) {}

      // 3. Merge new edits (adding multiple keys to ensure fallbacks work perfectly for Mysoft)
      const updatedShipping = {
        ...currentShipping,
        tc: formData?.tc,
        tc_kimlik: formData?.tc,
        phone: formData?.phone,
        telefon: formData?.phone,
        city: formData?.city,
        il: formData?.city,
        district: formData?.district,
        ilce: formData?.district,
        fullAddress: formData?.address,
        address: formData?.address,
        acikAdres: formData?.address
      };

      // 4. Save the enriched JSON back to the exact same order Mysoft reads from
      const { error: updateError } = await supabase?.from('orders')?.update({ shipping_address: JSON.stringify(updatedShipping) })?.eq('id', lastOrder?.id);

      if (updateError) throw updateError;

      // Also try to update basic phone in user_profiles if it exists (ignore silently if not)
      try { await supabase?.from('user_profiles')?.update({ phone: formData?.phone })?.eq('id', selectedUser?.id); } catch (_) {}

      setIsEditing(false);
      setEnrichedData(prev => ({ ...prev, ...formData }));
      alert("Kullanıcı bilgileri başarıyla güncellendi!");
    } catch (error) {
      console.error('Profil güncellenirken hata:', error);
      alert('Güncelleme Hatası: ' + error?.message);
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <>
      <div className="bg-card rounded-xl border border-border">
        <div className="p-4 md:p-6 border-b border-border">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl md:text-2xl font-semibold text-foreground">Kullanıcı Yönetimi</h2>
            {users?.filter(u => isUserBanned(u))?.length > 0 && (
              <button
                onClick={() => setStatusFilter(statusFilter === 'banned' ? 'all' : 'banned')}
                className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                  statusFilter === 'banned' ?'bg-error text-white' :'bg-error/10 text-error hover:bg-error/20 border border-error/30'
                }`}
              >
                <Icon name="Ban" className="w-4 h-4" />
                {users?.filter(u => isUserBanned(u))?.length} Yasaklı Kullanıcı
              </button>
            )}
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 md:gap-4">
            <Input
              type="search"
              placeholder="Kullanıcı ara..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e?.target?.value)}
            />
            <Select
              options={roleOptions}
              value={roleFilter}
              onChange={setRoleFilter}
              placeholder="Rol filtrele"
            />
            <Select
              options={statusOptions}
              value={statusFilter}
              onChange={setStatusFilter}
              placeholder="Durum filtrele"
            />
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-muted/50">
              <tr>
                <th className="text-left p-3 md:p-4 text-xs md:text-sm font-medium text-muted-foreground">Kullanıcı</th>
                <th className="text-left p-3 md:p-4 text-xs md:text-sm font-medium text-muted-foreground hidden md:table-cell">Rol</th>
                <th className="text-left p-3 md:p-4 text-xs md:text-sm font-medium text-muted-foreground hidden lg:table-cell">Üyelik</th>
                <th className="text-left p-3 md:p-4 text-xs md:text-sm font-medium text-muted-foreground hidden lg:table-cell">Durum</th>
                <th className="text-right p-3 md:p-4 text-xs md:text-sm font-medium text-muted-foreground">İşlemler</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {filteredUsers?.map((user) => (
                <tr key={user?.id} className={`transition-smooth ${isUserBanned(user) ? 'bg-error/5 hover:bg-error/10' : 'hover:bg-muted/30'}`}>
                  <td className="p-3 md:p-4">
                    <div className="flex items-center gap-3">
                      <div className="relative flex-shrink-0">
                        <Image
                          src={user?.avatar}
                          alt={user?.avatarAlt}
                          className={`w-8 h-8 md:w-10 md:h-10 rounded-full object-cover ${isUserBanned(user) ? 'opacity-50 grayscale' : ''}`}
                        />
                        {isUserBanned(user) && (
                          <span className="absolute -top-1 -right-1 w-4 h-4 bg-error rounded-full flex items-center justify-center">
                            <Icon name="Ban" className="w-2.5 h-2.5 text-white" />
                          </span>
                        )}
                      </div>
                      <div className="min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <p className={`text-sm md:text-base font-medium truncate ${isUserBanned(user) ? 'text-muted-foreground line-through' : 'text-foreground'}`}>{user?.name}</p>
                          {isUserBanned(user) && (
                            <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-xs font-semibold bg-error/15 text-error border border-error/30 flex-shrink-0">
                              <Icon name="Ban" className="w-2.5 h-2.5" />
                              Yasaklı
                            </span>
                          )}
                        </div>
                        <p className="text-xs md:text-sm text-muted-foreground truncate">{user?.email}</p>
                      </div>
                    </div>
                  </td>
                  <td className="p-3 md:p-4 hidden md:table-cell">
                    <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-md text-xs font-medium ${
                      user?.role === 'admin' ? 'bg-error/10 text-error' :
                      user?.role === 'organizer'? 'bg-secondary/10 text-secondary' : 'bg-muted text-muted-foreground'
                    }`}>
                      {user?.role === 'admin' ? 'Admin' : user?.role === 'organizer' ? 'Organizatör' : 'Kullanıcı'}
                    </span>
                  </td>
                  <td className="p-3 md:p-4 hidden lg:table-cell">
                    <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-md text-xs font-medium ${
                      user?.membershipTier === 'pro' ? 'bg-accent/10 text-accent-foreground' : 'bg-muted text-muted-foreground'
                    }`}>
                      <Icon name={user?.membershipTier === 'pro' ? 'Crown' : 'User'} className="w-3 h-3" />
                      {user?.membershipTier === 'pro' ? 'PRO Üye' : 'Ücretsiz'}
                    </span>
                  </td>
                  <td className="p-3 md:p-4 hidden lg:table-cell">
                    <div className="flex items-center gap-2">
                      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-md text-xs font-medium ${
                        user?.status === 'banned' ? 'bg-error/10 text-error' :
                        user?.status === 'pro' ? 'bg-accent/10 text-accent-foreground' :
                        'bg-success/10 text-success'
                      }`}>
                        {user?.status === 'banned' ? 'Yasaklı' : user?.status === 'pro' ? 'PRO' : 'Aktif'}
                      </span>
                    </div>
                  </td>
                  <td className="p-3 md:p-4">
                    <div className="flex items-center justify-end gap-2">
                      {/* Profile View Button */}
                      <Button
                        variant="ghost"
                        size="sm"
                        iconName="User"
                        onClick={() => handleViewProfile(user)}
                        className="hidden md:inline-flex text-primary hover:text-primary"
                        disabled={isProcessing}
                      >
                        Profil
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        iconName="User"
                        onClick={() => handleViewProfile(user)}
                        className="md:hidden text-primary"
                        disabled={isProcessing}
                      />

                      <Button
                        variant="ghost"
                        size="sm"
                        iconName="Award"
                        onClick={() => handleManageBadges(user)}
                        className="hidden md:inline-flex"
                        disabled={isProcessing}
                      >
                        Rozetler
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        iconName="Award"
                        onClick={() => handleManageBadges(user)}
                        className="md:hidden"
                        disabled={isProcessing}
                      />
                      
                      {/* Role Change Button */}
                      <Button
                        variant="ghost"
                        size="sm"
                        iconName="UserCog"
                        onClick={() => {
                          setSelectedUser(user);
                          setShowRoleModal(true);
                        }}
                        className="hidden md:inline-flex"
                        disabled={isProcessing}
                      >
                        Rol
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        iconName="UserCog"
                        onClick={() => {
                          setSelectedUser(user);
                          setShowRoleModal(true);
                        }}
                        className="md:hidden"
                        disabled={isProcessing}
                      />

                      {/* PRO Membership Toggle Button */}
                      <Button
                        variant="ghost"
                        size="sm"
                        iconName="Crown"
                        onClick={() => handlePromoteUser(user)}
                        className={`hidden md:inline-flex ${
                          user?.membershipTier === 'pro' ?'text-accent hover:text-accent/80' :'text-muted-foreground hover:text-accent'
                        }`}
                        disabled={isProcessing}
                      >
                        {user?.membershipTier === 'pro' ? 'PRO İptal' : 'PRO Yap'}
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        iconName="Crown"
                        onClick={() => handlePromoteUser(user)}
                        className={`md:hidden ${
                          user?.membershipTier === 'pro' ?'text-accent' :'text-muted-foreground'
                        }`}
                        disabled={isProcessing}
                      />
                      
                      {/* Ban/Unban Button */}
                      {!isUserBanned(user) ? (
                        <>
                          <Button
                            variant="ghost"
                            size="sm"
                            iconName="Ban"
                            onClick={() => handleBanToggle(user)}
                            className="hidden md:inline-flex text-error hover:text-error"
                            disabled={isProcessing}
                          >
                            Yasakla
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            iconName="Ban"
                            onClick={() => handleBanToggle(user)}
                            className="md:hidden text-error"
                            disabled={isProcessing}
                          />
                        </>
                      ) : (
                        <>
                          <Button
                            variant="ghost"
                            size="sm"
                            iconName="CheckCircle"
                            onClick={() => handleBanToggle(user)}
                            className="hidden md:inline-flex text-success hover:text-success"
                            disabled={isProcessing}
                          >
                            Yasağı Kaldır
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            iconName="CheckCircle"
                            onClick={() => handleBanToggle(user)}
                            className="md:hidden text-success"
                            disabled={isProcessing}
                          />
                        </>
                      )}
                      
                      {/* Delete Button */}
                      <Button
                        variant="ghost"
                        size="sm"
                        iconName="Trash2"
                        onClick={() => {
                          setSelectedUser(user);
                          setShowDeleteModal(true);
                        }}
                        className="hidden md:inline-flex text-error hover:text-error"
                        disabled={isProcessing}
                      >
                        Sil
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        iconName="Trash2"
                        onClick={() => {
                          setSelectedUser(user);
                          setShowDeleteModal(true);
                        }}
                        className="md:hidden text-error"
                        disabled={isProcessing}
                      />
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {filteredUsers?.length === 0 && (
          <div className="p-8 md:p-12 text-center">
            <Icon name="Users" size={48} className="mx-auto mb-4 text-muted-foreground opacity-50" />
            <p className="text-muted-foreground">Kullanıcı bulunamadı</p>
          </div>
        )}
      </div>
      {showBadgeModal && (
        <BadgeManagementModal
          user={selectedUser}
          onClose={handleBadgeModalClose}
          onUpdate={handleBadgeUpdate}
        />
      )}
      {/* User Profile Modal */}
      {showProfileModal && selectedUser && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
          <div className="bg-card rounded-xl border border-border w-full max-w-lg shadow-2xl max-h-[90vh] overflow-y-auto">
            {/* Modal Header */}
            <div className="flex items-center justify-between p-6 border-b border-border">
              <h3 className="text-xl font-semibold text-foreground">Kullanıcı Profili</h3>
              <div className="flex items-center gap-2">
                {!isEditing && (
                  <Button
                    variant="outline"
                    size="sm"
                    iconName="Edit"
                    onClick={() => setIsEditing(true)}
                  >
                    Profili Düzenle
                  </Button>
                )}
                <Button
                  variant="ghost"
                  size="icon"
                  iconName="X"
                  onClick={() => {
                    setShowProfileModal(false);
                    setSelectedUser(null);
                    setIsEditing(false);
                  }}
                />
              </div>
            </div>

            <div className="p-6 space-y-6">
              {/* Header: Avatar, Name, Email, Role */}
              <div className="flex items-center gap-4">
                <Image
                  src={selectedUser?.avatar}
                  alt={selectedUser?.avatarAlt || selectedUser?.name}
                  className="w-16 h-16 rounded-full object-cover flex-shrink-0 border-2 border-border"
                />
                <div className="min-w-0">
                  <h4 className="text-lg font-semibold text-foreground truncate">{selectedUser?.name || '—'}</h4>
                  <p className="text-sm text-muted-foreground truncate">{selectedUser?.email || '—'}</p>
                  <span className={`inline-flex items-center gap-1 mt-1 px-2 py-0.5 rounded-md text-xs font-medium ${
                    selectedUser?.role === 'admin' ? 'bg-error/10 text-error' :
                    selectedUser?.role === 'organizer'? 'bg-secondary/10 text-secondary' : 'bg-muted text-muted-foreground'
                  }`}>
                    {selectedUser?.role === 'admin' ? 'Admin' : selectedUser?.role === 'organizer' ? 'Organizatör' : 'Kullanıcı'}
                  </span>
                </div>
              </div>

              {/* Contact Section */}
              <div className="bg-muted/30 rounded-lg p-4 space-y-3">
                <h5 className="text-sm font-semibold text-foreground flex items-center gap-2">
                  <Icon name="Phone" size={16} className="text-primary" />
                  İletişim
                </h5>
                <div className="flex items-center gap-3">
                  <Icon name="Phone" size={15} className="text-muted-foreground flex-shrink-0" />
                  {isEditing ? (
                    <input
                      type="tel"
                      value={formData?.phone}
                      onChange={(e) => setFormData(prev => ({ ...prev, phone: e?.target?.value }))}
                      placeholder="Telefon numarası"
                      className="flex-1 text-sm bg-background border border-border rounded-md px-3 py-1.5 text-foreground focus:outline-none focus:ring-2 focus:ring-primary/50"
                    />
                  ) : (
                    <span className="text-sm text-foreground">
                      {enrichedData?.phone || selectedUser?.phone || selectedUser?.checkout_phone || <span className="text-muted-foreground italic">Telefon bilgisi yok</span>}
                    </span>
                  )}
                </div>
                {/* TC Kimlik / VKN */}
                <div className="flex items-center gap-3">
                  <Icon name="CreditCard" size={15} className="text-muted-foreground flex-shrink-0" />
                  <div className="flex-1">
                    <p className="text-xs text-muted-foreground mb-0.5">TC Kimlik / VKN</p>
                    {isEditing ? (
                      <input
                        type="text"
                        value={formData?.tc}
                        onChange={(e) => setFormData(prev => ({ ...prev, tc: e?.target?.value }))}
                        placeholder="TC Kimlik veya VKN"
                        className="w-full text-sm bg-background border border-border rounded-md px-3 py-1.5 text-foreground focus:outline-none focus:ring-2 focus:ring-primary/50"
                      />
                    ) : (
                      <p className="text-sm text-foreground font-medium">
                        {enrichedData === null
                          ? <span className="text-muted-foreground italic text-xs">Yükleniyor...</span>
                          : (enrichedData?.tc || selectedUser?.tc_kimlik || selectedUser?.tc || selectedUser?.vkn || selectedUser?.checkout_tc || <span className="text-muted-foreground italic">Belirtilmemiş</span>)
                        }
                      </p>
                    )}
                  </div>
                </div>
              </div>

              {/* Address Section */}
              <div className="bg-muted/30 rounded-lg p-4 space-y-3">
                <h5 className="text-sm font-semibold text-foreground flex items-center gap-2">
                  <Icon name="MapPin" size={16} className="text-primary" />
                  Adres Bilgileri
                </h5>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <p className="text-xs text-muted-foreground mb-0.5">İl</p>
                    {isEditing ? (
                      <input
                        type="text"
                        value={formData?.city}
                        onChange={(e) => setFormData(prev => ({ ...prev, city: e?.target?.value }))}
                        placeholder="İl"
                        className="w-full text-sm bg-background border border-border rounded-md px-3 py-1.5 text-foreground focus:outline-none focus:ring-2 focus:ring-primary/50"
                      />
                    ) : (
                      <p className="text-sm text-foreground font-medium">
                        {enrichedData === null
                          ? <span className="text-muted-foreground italic text-xs">Yükleniyor...</span>
                          : (enrichedData?.city || selectedUser?.city || selectedUser?.checkout_city || <span className="text-muted-foreground italic">Belirtilmemiş</span>)
                        }
                      </p>
                    )}
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground mb-0.5">İlçe</p>
                    {isEditing ? (
                      <input
                        type="text"
                        value={formData?.district}
                        onChange={(e) => setFormData(prev => ({ ...prev, district: e?.target?.value }))}
                        placeholder="İlçe"
                        className="w-full text-sm bg-background border border-border rounded-md px-3 py-1.5 text-foreground focus:outline-none focus:ring-2 focus:ring-primary/50"
                      />
                    ) : (
                      <p className="text-sm text-foreground font-medium">
                        {enrichedData === null
                          ? <span className="text-muted-foreground italic text-xs">Yükleniyor...</span>
                          : (enrichedData?.district || selectedUser?.district || selectedUser?.checkout_district || <span className="text-muted-foreground italic">Belirtilmemiş</span>)
                        }
                      </p>
                    )}
                  </div>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground mb-0.5">Açık Adres</p>
                  {isEditing ? (
                    <textarea
                      value={formData?.address}
                      onChange={(e) => setFormData(prev => ({ ...prev, address: e?.target?.value }))}
                      placeholder="Açık adres"
                      rows={3}
                      className="w-full text-sm bg-background border border-border rounded-md px-3 py-1.5 text-foreground focus:outline-none focus:ring-2 focus:ring-primary/50 resize-none"
                    />
                  ) : (
                    <p className="text-sm text-foreground">
                      {enrichedData === null
                        ? <span className="text-muted-foreground italic text-xs">Yükleniyor...</span>
                        : (enrichedData?.address || selectedUser?.full_address || selectedUser?.checkout_address || <span className="text-muted-foreground italic">Adres bilgisi yok</span>)
                      }
                    </p>
                  )}
                </div>
              </div>

              {/* Membership Info Section */}
              <div className="bg-muted/30 rounded-lg p-4 space-y-3">
                <h5 className="text-sm font-semibold text-foreground flex items-center gap-2">
                  <Icon name="Crown" size={16} className="text-primary" />
                  Üyelik Bilgileri
                </h5>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <p className="text-xs text-muted-foreground mb-0.5">Kayıt Tarihi</p>
                    <p className="text-sm text-foreground font-medium">
                      {selectedUser?.joinDate || <span className="text-muted-foreground italic">—</span>}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground mb-0.5">Üyelik Durumu</p>
                    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-xs font-medium ${
                      selectedUser?.status === 'banned' ? 'bg-error/10 text-error' :
                      selectedUser?.membershipTier === 'pro' ? 'bg-accent/10 text-accent-foreground' :
                      'bg-success/10 text-success'
                    }`}>
                      <Icon name={selectedUser?.membershipTier === 'pro' ? 'Crown' : selectedUser?.status === 'banned' ? 'Ban' : 'CheckCircle'} className="w-3 h-3" />
                      {selectedUser?.status === 'banned' ? 'Yasaklı' : selectedUser?.membershipTier === 'pro' ? 'PRO Üye' : 'Aktif'}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            <div className="px-6 pb-6 flex gap-2">
              {isEditing ? (
                <>
                  <Button
                    variant="outline"
                    className="flex-1"
                    onClick={() => setIsEditing(false)}
                    disabled={isProcessing}
                  >
                    İptal
                  </Button>
                  <Button
                    variant="default"
                    className="flex-1 bg-primary hover:bg-primary/90 text-primary-foreground"
                    iconName={isProcessing ? 'Loader2' : 'Save'}
                    onClick={handleSaveProfile}
                    disabled={isProcessing}
                  >
                    {isProcessing ? 'Kaydediliyor...' : 'Kaydet'}
                  </Button>
                </>
              ) : (
                <Button
                  variant="outline"
                  className="w-full"
                  onClick={() => {
                    setShowProfileModal(false);
                    setSelectedUser(null);
                    setIsEditing(false);
                  }}
                >
                  Kapat
                </Button>
              )}
            </div>
          </div>
        </div>
      )}
      {/* Role Change Modal */}
      {showRoleModal && selectedUser && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
          <div className="bg-card rounded-xl border border-border p-6 max-w-md w-full shadow-2xl">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-semibold text-foreground">Kullanıcı Rolünü Değiştir</h3>
              <Button
                variant="ghost"
                size="icon"
                iconName="X"
                onClick={() => {
                  setShowRoleModal(false);
                  setSelectedUser(null);
                }}
              />
            </div>
            
            <div className="mb-6">
              <p className="text-sm text-muted-foreground mb-4">
                <span className="font-medium text-foreground">{selectedUser?.name}</span> kullanıcısının rolünü değiştirin
              </p>
              
              <div className="space-y-2">
                <button
                  onClick={() => handleRoleChange(selectedUser, 'user')}
                  disabled={isProcessing || selectedUser?.role === 'user'}
                  className="w-full flex items-center gap-3 p-3 rounded-lg border border-border hover:bg-muted transition-smooth disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <Icon name="User" size={20} />
                  <div className="text-left">
                    <div className="font-medium text-foreground">Kullanıcı</div>
                    <div className="text-xs text-muted-foreground">Standart kullanıcı yetkisi</div>
                  </div>
                </button>
                
                <button
                  onClick={() => handleRoleChange(selectedUser, 'organizer')}
                  disabled={isProcessing || selectedUser?.role === 'organizer'}
                  className="w-full flex items-center gap-3 p-3 rounded-lg border border-border hover:bg-muted transition-smooth disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <Icon name="Calendar" size={20} />
                  <div className="text-left">
                    <div className="font-medium text-foreground">Organizatör</div>
                    <div className="text-xs text-muted-foreground">Etkinlik oluşturma yetkisi</div>
                  </div>
                </button>
                
                <button
                  onClick={() => handleRoleChange(selectedUser, 'admin')}
                  disabled={isProcessing || selectedUser?.role === 'admin'}
                  className="w-full flex items-center gap-3 p-3 rounded-lg border border-border hover:bg-muted transition-smooth disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <Icon name="ShieldCheck" size={20} />
                  <div className="text-left">
                    <div className="font-medium text-foreground">Admin</div>
                    <div className="text-xs text-muted-foreground">Tam yönetici yetkisi</div>
                  </div>
                </button>
              </div>
            </div>
            
            <div className="flex gap-2">
              <Button
                variant="outline"
                className="flex-1"
                onClick={() => {
                  setShowRoleModal(false);
                  setSelectedUser(null);
                }}
                disabled={isProcessing}
              >
                İptal
              </Button>
            </div>
          </div>
        </div>
      )}
      {/* Delete Confirmation Modal */}
      {showDeleteModal && selectedUser && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
          <div className="bg-card rounded-xl border border-border p-6 max-w-md w-full shadow-2xl">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-semibold text-foreground">Kullanıcıyı Sil</h3>
              <Button
                variant="ghost"
                size="icon"
                iconName="X"
                onClick={() => {
                  setShowDeleteModal(false);
                  setSelectedUser(null);
                }}
                disabled={isProcessing}
              />
            </div>
            
            <div className="mb-6">
              <div className="flex items-center gap-3 p-4 bg-error/10 rounded-lg border border-error/20 mb-4">
                <Icon name="AlertTriangle" size={24} className="text-error flex-shrink-0" />
                <div>
                  <p className="text-sm font-medium text-error">Bu işlem geri alınamaz!</p>
                  <p className="text-xs text-error/80 mt-1">Kullanıcının tüm verileri kalıcı olarak silinecektir.</p>
                </div>
              </div>
              
              <p className="text-sm text-muted-foreground mb-2">
                <span className="font-medium text-foreground">{selectedUser?.name}</span> kullanıcısını silmek istediğinizden emin misiniz?
              </p>
              
              <div className="text-xs text-muted-foreground space-y-1 mt-3">
                <p>• Kullanıcı profili silinecek</p>
                <p>• Tüm etkinlik kayıtları silinecek</p>
                <p>• Tüm mesajlar silinecek</p>
                <p>• Tüm yorumlar ve değerlendirmeler silinecek</p>
              </div>
            </div>
            
            <div className="flex gap-2">
              <Button
                variant="outline"
                className="flex-1"
                onClick={() => {
                  setShowDeleteModal(false);
                  setSelectedUser(null);
                }}
                disabled={isProcessing}
              >
                İptal
              </Button>
              <Button
                variant="destructive"
                className="flex-1 bg-error hover:bg-error/90 text-white"
                onClick={handleDeleteUser}
                disabled={isProcessing}
                iconName={isProcessing ? 'Loader2' : 'Trash2'}
              >
                {isProcessing ? 'Siliniyor...' : 'Sil'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default UserManagementTable;